import { useSyncExternalStore } from "react";
import { FLOW_PAGES_CACHE } from "@/lib/swRoutes";

// Recovery from a stale app shell that asks for a lazy chunk a later deploy deleted
// ("Failed to fetch dynamically imported module: …/assets/RowComments-<hash>.js").
//
// How a page ends up on an old build (measured locally, see the PR): a tab stays open
// across a deploy, or (until the service worker dropped its 3 s navigation timeout, see
// lib/swCaching.ts) a navigation to a URL visited before was answered from the "flow-pages"
// cache because the network was slow. The
// old build still works while its own service worker is active. As soon as the new
// service worker activates (skipWaiting + clientsClaim) it purges the old precache, and
// once the browser's HTTP cache no longer holds the old file either, every lazy chunk the
// old page has not loaded yet 404s.
//
// The cure is a reload onto the current build. Rules:
//   * reload at most ONCE per page, and never twice within 30 s across reloads (a
//     sessionStorage timestamp), so a still-broken deployment shows a message instead of
//     reloading forever;
//   * the 30 s also count from the page's START: a page that a recovery reload opened
//     never reloads by itself again, however slowly its own failure arrives (a chunk that
//     answers 404 only after 31 s would otherwise buy one reload per page, a loop);
//   * if the guard cannot be stored (storage blocked or throwing), do not reload at all;
//   * before reloading, ask the service worker to update and drop the cached copy of the
//     current URL, so the reload cannot be answered with the same stale shell again;
//     both steps are capped so a hanging update never blocks the reload;
//   * if that update is REJECTED (offline, or the frontend container is being swapped and
//     nginx answers 502), do not reload by itself: the reload would land on the browser's
//     offline page or the 502 page. The message with "Neu laden" stays instead.
//
// What a reload costs: unsaved typed text that is not persisted is lost. There is no
// app-wide "dirty draft" flag to consult today (comment composer drafts survive, they are
// stored per row in localStorage); see the PR for which editors can be open when a lazy
// chunk loads.

export const CHUNK_RELOAD_KEY = "flow:chunk-reload-at";
export const CHUNK_RELOAD_WINDOW_MS = 30_000;
export const RELOAD_PREPARE_TIMEOUT_MS = 3_000;

// Chrome/Edge, Firefox, Safari wording for a failed dynamic import, plus Vite's own
// message for a failed CSS preload of a lazy chunk.
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(err.message));
}

export interface GuardStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** True when a reload may happen now (and records it); false when one happened within
 * the window of this failure or of this page's start, or the guard cannot be stored.
 * `now` and `pageStart` are wall-clock times in the stamp's clock (Date.now()). */
export function claimReloadSlot(
  storage: GuardStorage | null,
  now: number,
  pageStart: number,
): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0) {
      // Math.abs: the clock may have been set back since the stamp; a stamp from the
      // "future" must not block every recovery in this tab until the clock catches up.
      if (Math.abs(now - last) < CHUNK_RELOAD_WINDOW_MS) return false;
      // This page was opened by that reload (it starts a few seconds after the stamp).
      if (Math.abs(pageStart - last) < CHUNK_RELOAD_WINDOW_MS) return false;
    }
    const stamp = String(now);
    storage.setItem(CHUNK_RELOAD_KEY, stamp);
    // A guard that did not stick cannot stop a loop.
    return storage.getItem(CHUNK_RELOAD_KEY) === stamp;
  } catch {
    return false;
  }
}

export type RecoveryPhase = "idle" | "reloading" | "failed";

export interface ChunkRecoveryDeps {
  getStorage: () => GuardStorage | null;
  now: () => number;
  /** When this page (this JS realm) started, in the clock of `now`. */
  pageStart: () => number;
  /** Ask the service worker to update and drop the stale cached shell. Resolves false when
   * the server could not be reached (the update was rejected). */
  prepareReload: () => Promise<boolean>;
  reload: () => void;
}

export interface ChunkRecovery {
  /** Guarded automatic recovery; returns the resulting phase. Idempotent per page. */
  recover(): RecoveryPhase;
  /** Explicit "Neu laden" on the message: reload without consulting the guard; recorded
   * like an automatic reload, so a persistent failure costs one reload per click. */
  reloadNow(): void;
  /** "Neu laden" on the "Neue Version verfügbar" toast (lib/updatePrompt.ts): a plain
   * reload onto the new version that records no stamp. */
  reloadForUpdate(): void;
  getPhase(): RecoveryPhase;
  subscribe(listener: () => void): () => void;
}

export function createChunkRecovery(deps: ChunkRecoveryDeps): ChunkRecovery {
  let phase: RecoveryPhase = "idle";
  const listeners = new Set<() => void>();

  const setPhase = (next: RecoveryPhase) => {
    if (phase === next) return;
    phase = next;
    for (const l of listeners) l();
  };

  let reloadStarted = false;
  /** automatic = the guarded recovery; it gives up when the server cannot be reached.
   * The explicit "Neu laden" always reloads (offline it lands on the precached shell). */
  const startReload = (automatic: boolean) => {
    if (reloadStarted) return;
    reloadStarted = true;
    let prepared: Promise<boolean>;
    try {
      // An unexpected failure of the preparation itself says nothing about the server.
      prepared = deps.prepareReload().catch(() => true);
    } catch {
      prepared = Promise.resolve(true);
    }
    // A HANGING update (stalled connection) still reloads after the cap.
    const cap = new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), RELOAD_PREPARE_TIMEOUT_MS),
    );
    void Promise.race([prepared, cap]).then((reachable) => {
      if (automatic && reachable === false) {
        reloadStarted = false; // the button may still reload
        setPhase("failed");
        return;
      }
      deps.reload();
    });
  };

  return {
    recover() {
      if (phase !== "idle") return phase;
      if (!claimReloadSlot(deps.getStorage(), deps.now(), deps.pageStart())) {
        setPhase("failed");
        return phase;
      }
      setPhase("reloading");
      startReload(true);
      return phase;
    },
    reloadNow() {
      // Recorded like an automatic reload, so the page this click opens does not reload
      // by itself again if the failure persists: one click, one reload. A stamp that
      // cannot be written never blocks the click.
      try {
        deps.getStorage()?.setItem(CHUNK_RELOAD_KEY, String(deps.now()));
      } catch {
        /* storage blocked or full: reload anyway */
      }
      setPhase("reloading");
      startReload(false);
    },
    reloadForUpdate() {
      // No stamp: this follows a deploy, not a failure. The page it opens starts within
      // 30 s of the click; a stamp would block that page's automatic recovery for its whole
      // life (the page-start rule), e.g. when the next deploy removes one of its chunks.
      setPhase("reloading");
      startReload(false);
    },
    getPhase: () => phase,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Vite dispatches `vite:preloadError` on window when a lazy chunk (or its CSS) fails to
 * load. The event is deliberately NOT cancelled: the original error keeps propagating to
 * the route error boundary, which shows the German message (reloading or, if the guard
 * refused, with a "Neu laden" button). */
export function listenForPreloadErrors(target: EventTarget, recovery: ChunkRecovery): () => void {
  const onError = () => {
    recovery.recover();
  };
  target.addEventListener("vite:preloadError", onError);
  return () => target.removeEventListener("vite:preloadError", onError);
}

function browserStorage(): GuardStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Resolves false only when the service worker's update was rejected: its script could not
 * be fetched (offline, or the server answered with an error such as nginx's 502 while the
 * frontend container is swapped). No service worker, or no registration: true. */
export async function prepareBrowserReload(): Promise<boolean> {
  let reachable = true;
  const jobs: Promise<unknown>[] = [];
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    jobs.push(
      navigator.serviceWorker.getRegistration().then((reg) =>
        reg?.update().catch(() => {
          reachable = false;
        }),
      ),
    );
  }
  if (typeof caches !== "undefined") {
    const url = window.location.href;
    jobs.push(
      caches
        .has(FLOW_PAGES_CACHE)
        .then((has) => (has ? caches.open(FLOW_PAGES_CACHE).then((c) => c.delete(url)) : false)),
    );
  }
  await Promise.allSettled(jobs);
  return reachable;
}

// The page's navigation start in the stamp's clock, read once while the app boots
// (seconds after navigation start, so no sleep or clock change lies in between).
// performance.timeOrigin is the same moment in principle, but browsers derive it from a
// monotonic clock base that can drift far from Date.now() (e.g. after the computer
// slept, while the renderer process and its base survive a reload); comparing it with a
// Date.now() stamp could then miss the reload this page came from.
const pageStartedAt = Date.now() - (typeof performance !== "undefined" ? performance.now() : 0);

export const chunkRecovery: ChunkRecovery = createChunkRecovery({
  getStorage: browserStorage,
  now: () => Date.now(),
  pageStart: () => pageStartedAt,
  prepareReload: prepareBrowserReload,
  reload: () => window.location.reload(),
});

export function useChunkRecoveryPhase(): RecoveryPhase {
  return useSyncExternalStore(
    chunkRecovery.subscribe,
    chunkRecovery.getPhase,
    chunkRecovery.getPhase,
  );
}
